
import StripePayment from '@/services/payments/StripePayment';
import cartMixin from '@/mixins/cartMixin';
import addressMixin from '@/mixins/addressMixin';
import checkoutPaymentMixin from '@/mixins/checkoutPaymentMixin';
import { loadStripe } from '@stripe/stripe-js';
import { CarriersService } from '@/services/CarriersService.js';
import { PaymentMethods } from '@/services/payments/PaymentMethods';

export default {
  mixins: [cartMixin, addressMixin, checkoutPaymentMixin],
  props: {
    paymentMethodTypes: {
      type: String,
      default: 'card,paypal',
    },
  },
  data() {
    return {
      stripe: null,
      elements: null,
      clientSecret: null,
      loading: true,
      error: null,
      expressCheckoutElement: null,
      initializedForKey: null,
      initializing: false,
      carriersService: new CarriersService({
        http: this.$http,
        auth: this.$auth,
        store: this.$store,
      }),
      paymentService: new PaymentMethods({
        http: this.$http,
        auth: this.$auth,
        store: this.$store,
      }),
    };
  },
  computed: {
    stripePayment() {
      return new StripePayment({
        cart: this.cart,
        auth: this.$auth,
        http: this.$http,
        addressService: this.addressService,
      });
    },
    canInitializeExpress() {
      return Boolean(
        this.stripe &&
          this.cart?.IdCart &&
          Number(this.cart?.Total?.ToPay?.TaxIncl || 0) > 0
      );
    },
    expressInitKey() {
      const idCart = this.cart?.IdCart || 'no-cart';
      const amount = Number(this.cart?.Total?.ToPay?.TaxIncl || 0);
      const currency = this.cart?.Currency?.IsoCode || '';
      const locale = this.$i18n?.locale || 'fr';
      return `${idCart}-${amount}-${currency}-${locale}-${this.paymentMethodTypes}`;
    },
  },
  mounted() {
    const _stripe = loadStripe(process.env.STRIPE_PUBLIC_KEY);
    _stripe.then((stripe) => {
      this.stripe = stripe;
      console.log('Stripe loaded', stripe);
      setTimeout(() => {
        this.maybeInitialize();
      }, 2000);
    });
  },
  beforeDestroy() {
    this.destroyExpressElement();
  },
  watch: {
    expressInitKey() {
      // this.maybeInitialize();
    },
  },
  methods: {
    destroyExpressElement() {
      if (!this.expressCheckoutElement) return;
      this.expressCheckoutElement.destroy();
      this.expressCheckoutElement = null;
    },
    async maybeInitialize() {
      if (!this.canInitializeExpress) {
        this.destroyExpressElement();
        this.loading = false;
        return;
      }

      if (this.initializing || this.initializedForKey === this.expressInitKey) {
        return;
      }

      this.initializing = true;
      this.loading = true;
      this.error = null;
      this.destroyExpressElement();

      await this.initialize();

      this.initializedForKey = this.expressCheckoutElement
        ? this.expressInitKey
        : null;
      this.initializing = false;
      this.loading = false;
    },
    mapCarrierGroupsToStripeRates(carrier = {}) {
      const groups = [
        ...(carrier.Store || []).map((item) => ({ ...item, _group: 'store' })),
        ...(carrier.RelayPoint || []).map((item) => ({
          ...item,
          _group: 'relay',
        })),
        ...(carrier.Home || []).map((item) => ({ ...item, _group: 'home' })),
      ];

      return groups.map((item) => {
        let displayName = item.Title || item.Name || 'Livraison';

        if (item._group === 'store' && item.StoreName) {
          displayName = `Retrait boutique - ${item.StoreName}`;
        }

        if (item._group === 'relay') {
          displayName = item.Title || `Point relais - ${item.Name}`;
        }

        if (item._group === 'home') {
          displayName = item.Title || `Domicile - ${item.Name}`;
        }

        return {
          id: `${item._group}_${item.IdCarrier}`,
          displayName,
          amount: Math.round(Number(item?.Price?.TaxIncl || 0) * 100),
        };
      });
    },
    async handleShippingAddressChange(event) {
      try {
        const address = event.address;

        console.log('shipping address changed:', event, address);

        // هنا تقدر تخزنها مؤقتاً ف data()
        //         {
        //     "city": "Mountain View",
        //     "state": "CA",
        //     "postal_code": "94043",
        //     "country": "US"
        // }
        // this.selectedShippingAddress = address;

        const response = await this.carriersService.getCarriers({
          postcode: address.postal_code,
          city: address.city,
          address1: address.city,
          country: address.country,
          countryIsoCode: address.country,
          languageIsoCode: this.$i18n.locale,
        });
        // const response = await this.carriersService.getCarriers({
        //   postcode: 75006,
        //   city: 'Paris',
        //   address1: "12, Rue de l'École de Médecine",
        //   country: 'FR',
        //   countryIsoCode: 'FR',
        //   languageIsoCode: 'fr',
        // });

        console.log('response:', response);

        const shippingRates = this.mapCarrierGroupsToStripeRates(
          response.Carrier
        );
        console.log('shippingRates for Stripe:', shippingRates);

        if (!shippingRates.length) {
          return event.reject();
        }

        event.resolve({
          shippingRates,
        });
      } catch (err) {
        console.error(err);
        event.reject();
      }
    },
    async initialize() {
      try {
        const data = await this.stripePayment.intent({
          paymentMethodTypes: this.paymentMethodTypes,
          isDigital: this.isDigitalCart,
        });

        this.clientSecret = data.ClientSecret;

        if (this.clientSecret) {
          this.elements = this.stripe.elements({
            clientSecret: this.clientSecret,
            locale: this.$i18n.locale ?? 'fr',
          });

          this.expressCheckoutElement = this.createExpressCheckoutElement();

          this.expressCheckoutElement.mount('#express-checkout-element');

          this.expressCheckoutElement.on(
            'ready',
            ({ availablePaymentMethods }) => {
              const hasAvailableMethod =
                availablePaymentMethods &&
                Object.values(availablePaymentMethods).some(Boolean);

              if (!hasAvailableMethod) {
                this.error =
                  'No express payment methods are available on this device/browser.';
              }
            }
          );

          this.expressCheckoutElement.on('confirm', this.handleConfirm);
          this.expressCheckoutElement.on(
            'shippingaddresschange',
            this.handleShippingAddressChange
          );
          this.expressCheckoutElement.on(
            'shippingratechange',
            async (event) => {
              try {
                const shippingRate = event.shippingRate;

                // subtotal panier
                const subtotal = 10000; // 100€

                // prix livraison choisi
                const shippingAmount = shippingRate.amount;

                // nouveau total
                const total = subtotal + shippingAmount;

                event.resolve({
                  amount: total, // 🔥 important
                  lineItems: [
                    {
                      name: 'Sous-total',
                      amount: subtotal,
                    },
                    {
                      name: 'Livraison',
                      amount: shippingAmount,
                    },
                  ],
                });
              } catch (e) {
                event.reject();
              }
            }
          );
        }
      } catch (error) {
        console.error('stripe express checkout error: ', error);
        this.error = error.message || error;
        this.initializedForKey = null;
      }
    },
    createExpressCheckoutElement() {
      const orderedOptions = {
        paymentMethodOrder: ['paypal', 'google_pay', 'apple_pay'],
        requestPayerName: true,
        requestPayerEmail: true,
        requestShipping: true,
        emailRequired: true,
        shippingAddressRequired: true,
        paymentMethods: {
          paypal: 'auto',
          googlePay: 'auto',
          applePay: 'auto',
          link: 'never',
        },
      };

      try {
        return this.elements.create('expressCheckout', orderedOptions);
      } catch (e) {
        return this.elements.create('expressCheckout', {
          requestPayerName: true,
          requestPayerEmail: true,
          requestShipping: true,
          emailRequired: true,
          shippingAddressRequired: true,
          paymentMethods: {
            paypal: 'auto',
            googlePay: 'auto',
            applePay: 'auto',
            link: 'never',
          },
        });
      }
    },
    async handleConfirm(event) {
      if (this.submitLoading) return;
      this.submitLoading = true;
      this.toggleLoader(true);

      try {
        const email =
          event?.email ||
          event?.payerEmail ||
          event?.billingDetails?.email ||
          event?.paymentMethod?.billing_details?.email ||
          this.$auth?.user?.Email ||
          this.guestUser?.Customer?.Email ||
          '';

        const name =
          event?.name ||
          event?.payerName ||
          event?.billingDetails?.name ||
          event?.paymentMethod?.billing_details?.name ||
          '';

        const shippingAddress = event?.shippingAddress || null;
        const shippingRate = event?.shippingRate || null;

        console.log('Express checkout confirm event:', {
          email,
          name,
          shippingAddress,
          shippingRate,
          event,
        });

        if (!email) {
          throw new Error(this.$t('Email is required to complete the order.'));
        }

        // Parse name into Firstname and Lastname
        const nameParts = (name || '').trim().split(' ');
        const firstname = nameParts[0] || 'Guest';
        const lastname =
          nameParts.length > 1 ? nameParts.slice(1).join(' ') : '.';

        // Prepare address info for registration/address creation
        const addressInfo = {
          Firstname: firstname,
          Lastname: lastname,
          Address1: shippingAddress?.address?.line1 || '',
          Address2: shippingAddress?.address?.line2 || '',
          Postcode: shippingAddress?.address?.postal_code || '',
          City: shippingAddress?.address?.city || '',
          CountryIsoCode: shippingAddress?.address?.country || '',
          MobilePhone: shippingAddress?.phone || '0600000000', // Default if missing
          IsDelivery: true,
          IsInvoice: true,
        };

        // 1. If not logged in, register as guest
        if (!this.isLoggedIn) {
          const registrationData = {
            account: {
              email: email,
              firstname: firstname,
              lastname: lastname,
              addressInfo: addressInfo,
            },
            languageIsoCode: this.$i18n.locale,
            currencyIsoCode: this.cart?.Currency?.IsoCode,
          };
          console.log('Registering guest with data:', registrationData);
          await this.customerService.registerGuest(registrationData);
        }

        // 2. Set the selected carrier to the cart
        if (shippingRate) {
          const [group, idCarrier] = shippingRate.id.split('_');
          await this.cartService.setCarrierToCart({
            idCarrier: idCarrier,
            countryIsoCode: addressInfo.CountryIsoCode,
          });
          // Important: fetch cart after setting carrier to get updated totals and carrier info
          await this.cartService.fetchCart();
        }

        // 3. Ensure addresses are loaded in the service for postData
        await this.addressService.fetchAddresses();

        // 3b. Ensure payment methods are loaded to find Stripe's ID
        if (!this.$store.state.checkout.paymentMethods?.length) {
          const pmResponse = await this.paymentService.list({
            languageIsoCode: this.$i18n.locale,
            idAddress: this.idAddressDelivery,
          });
          this.$store.commit('checkout/setPaymentMethods', {
            data: pmResponse?.PaymentMethods || [],
          });
        }

        // 4. Create Order in the backend (postData)
        // Find Stripe payment method to get its IdPayment
        let stripeMethod = this.$store.state.checkout.paymentMethods?.find(
          (m) => m.PaymentProvider === 'Stripe' || m.PaymentCode === 'STRIPE'
        );

        // Fallback: try to find any stripe-like method if not found
        if (!stripeMethod) {
          stripeMethod = this.$store.state.checkout.paymentMethods?.find((m) =>
            m.PaymentName?.toLowerCase().includes('stripe')
          );
        }

        await this.stripePayment.postData({
          clientSecret: this.clientSecret,
          paymentMethod: stripeMethod,
        });

        // 5. Confirm the Stripe payment
        const idorder = this.cart.IdCart;
        const thanksQuery = this.getThanksQuery({
          orderid: idorder,
          cartid: this.cartService.idCart,
        });
        const thanksReturnUrl = `${process.env.URL}cart/thanks/accepted?${thanksQuery}`;

        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          clientSecret: this.clientSecret,
          confirmParams: {
            return_url: thanksReturnUrl,
          },
          redirect: 'if_required',
        });

        if (error) {
          console.error('Stripe confirmPayment error:', error);
          this.showMessage(error.message);
        } else if (paymentIntent && paymentIntent.status === 'succeeded') {
          this.addPaymentInfoGtm('Express Checkout');

          this.$router.push({
            path: this.localePath(
              `/cart/thanks/accepted?${thanksQuery}&express_status=succeeded`
            ),
          });
        }
      } catch (err) {
        console.error('Express checkout handleConfirm error:', err);
        const apiError =
          err.response?.data?.Message ||
          err.message ||
          'An error occurred during checkout.';
        this.showMessage(apiError);
      } finally {
        this.submitLoading = false;
        this.toggleLoader(false);
      }
    },
    showMessage(messageText) {
      if (process.client) {
        const messageContainer = document.querySelector(
          '#express-payment-message'
        );
        messageContainer.classList.remove('hidden');
        messageContainer.textContent = messageText;

        setTimeout(() => {
          messageContainer.classList.add('hidden');
          messageContainer.textContent = '';
        }, 4000);
      }
    },
    getThanksQuery(query = {}) {
      const searchParams = new URLSearchParams({
        ...query,
        payment_source: 'expresscheckout',
      });
      return searchParams.toString();
    },
    toggleLoader(isLoading) {
      this.$store.commit('shared/wind/setIsLoading', isLoading);
    },
  },
};
